import React, { useState } from 'react';
import { PageComponent } from './ComponentLibrary';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import FAQEditor from './componentEditors/FAQEditor';
import NewsletterEditor from './componentEditors/NewsletterEditor';
import BankListEditor from './componentEditors/BankListEditor';
import TextBlockEditor from './componentEditors/TextBlockEditor';
import ImageBlockEditor from './componentEditors/ImageBlockEditor';
import CallToActionEditor from './componentEditors/CallToActionEditor';
import LoanCalculatorEditor from './componentEditors/LoanCalculatorEditor';
import BankComparisonEditor from './componentEditors/BankComparisonEditor';
import CostTableEditor from './componentEditors/CostTableEditor';
import InterestRatesEditor from './componentEditors/InterestRatesEditor';
import HeaderEditor from './componentEditors/HeaderEditor';
import FooterEditor from './componentEditors/FooterEditor';

interface ComponentSettingsProps {
  component: PageComponent;
  onChange: (updates: Partial<PageComponent>) => void;
}

const ComponentSettings: React.FC<ComponentSettingsProps> = ({ component, onChange }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ title: e.target.value });
  };
  
  const handleSettingsChange = (settings: any) => {
    onChange({ settings: { ...component.settings, ...settings } });
  };
  
  const handleContentChange = (content: any) => {
    onChange({ content: { ...component.content, ...content } });
  };
  
  const renderComponentEditor = () => {
    switch (component.type) {
      case 'header':
        return (
          <HeaderEditor 
            settings={component.settings} 
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'footer':
        return (
          <FooterEditor 
            settings={component.settings} 
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'faq':
        return (
          <FAQEditor 
            settings={component.settings} 
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'newsletter':
        return (
          <NewsletterEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'bankList':
        return (
          <BankListEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'textBlock':
        return (
          <TextBlockEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'imageBlock':
        return (
          <ImageBlockEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'callToAction':
        return (
          <CallToActionEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'loanCalculator':
        return (
          <LoanCalculatorEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'bankComparison':
        return (
          <BankComparisonEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'costTable':
        return (
          <CostTableEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      case 'interestRates':
        return (
          <InterestRatesEditor 
            settings={component.settings}
            content={component.content}
            onSettingsChange={handleSettingsChange}
            onContentChange={handleContentChange}
          />
        );
      default:
        return (
          <div className="p-4 text-center text-slate-500">
            Bu bileşen türü için düzenleme arayüzü henüz oluşturulmadı.
          </div>
        );
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          Bileşen Başlığı (Sadece yönetim panelinde görünür)
        </label>
        <input
          type="text"
          value={component.title}
          onChange={handleTitleChange}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
        />
      </div>
      
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">Genel</TabsTrigger>
          <TabsTrigger value="content">İçerik</TabsTrigger>
          <TabsTrigger value="style">Görünüm</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-4 pt-4">
          {renderComponentEditor()}
        </TabsContent>
        
        <TabsContent value="content" className="space-y-4 pt-4">
          {renderComponentEditor()}
        </TabsContent>
        
        <TabsContent value="style" className="space-y-4 pt-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              Görünüm ayarları bu bileşen türü için kullanılabilir değil.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComponentSettings;